import React, { useState } from "react";
import { Link } from "react-router-dom";

import { PageSection } from "@/components/AppLayout.jsx";
import { AdminGuard } from "@/components/admin/AdminGuard.jsx";
import { GsdReadableBlock } from "@/coquille-bridge";
import { useI18n } from "@/coquille-bridge";
import {
  getPresentationUi,
  getPhase1Limits,
  getPhase2Preview,
  getNoteTypeLabel,
  NOTE_TYPES,
  PRESENTATION_CHECKLIST,
} from "@/content/interactivePresentation038.js";
import { useInteractivePresentation038 } from "@/hooks/useInteractivePresentation038.js";
import { useSpeechDictation } from "@/hooks/useSpeechDictation.js";

function downloadText(filename, content, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function InteractivePresentationPage() {
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  const ui = getPresentationUi(locale);
  const limits = getPhase1Limits(locale);
  const phase2 = getPhase2Preview(locale);
  const noteTypes = NOTE_TYPES[locale];

  const {
    state,
    blocks,
    activeBlock,
    stepNotes,
    draftNote,
    setDraftNote,
    editingNoteId,
    setEditingNoteId,
    reportText,
    setTrack,
    goStep,
    markComplete,
    resetDemo,
    toggleTour,
    addNote,
    updateNote,
    deleteNote,
    resetNotes,
    generateReport,
    exportJson,
  } = useInteractivePresentation038(locale);

  const { voiceSupported, dictating, startDictation, stopDictation } = useSpeechDictation(
    locale,
    draftNote.text,
    setDraftNote,
  );

  const [copyMsg, setCopyMsg] = useState("");

  const progressPct = blocks.length ? Math.round((state.completedIds.length / blocks.length) * 100) : 0;

  const handleCopyReport = async () => {
    const md = reportText || generateReport();
    try {
      await navigator.clipboard.writeText(md);
      setCopyMsg(ui.copied);
      setTimeout(() => setCopyMsg(""), 2500);
    } catch {
      setCopyMsg(locale === "en" ? "Copy failed — use export." : "Copie impossible — utilisez l'export.");
    }
  };

  const primaryScreen = activeBlock?.screens?.[0];

  return (
    <AdminGuard>
      <PageSection
        title={ui.pageTitle}
        subtitle={ui.pageSubtitle}
        testId="page-admin-presentation-interactive"
      >
        <GsdReadableBlock className="ip-intro" testId="ip-intro">
          <p>{ui.intro}</p>
        </GsdReadableBlock>

        <section className="ip-checklist" data-testid="ip-checklist">
          <h2 className="section-h2">{ui.checklistTitle}</h2>
          <ul className="ip-checklist__list">
            {(locale === "en" ? PRESENTATION_CHECKLIST.en : PRESENTATION_CHECKLIST.fr).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="ip-tracks" data-testid="ip-tracks">
          <h2 className="section-h2">{locale === "en" ? "Choose a track" : "Choisir un parcours"}</h2>
          <div className="ip-tracks__grid">
            <button
              type="button"
              className={state.trackId === "express" ? "ip-track-card ip-track-card--active" : "ip-track-card"}
              data-testid="ip-track-express"
              onClick={() => setTrack("express")}
            >
              <strong>{ui.trackExpress}</strong>
              <span className="muted">{ui.trackExpressHint}</span>
            </button>
            <button
              type="button"
              className={state.trackId === "full" ? "ip-track-card ip-track-card--active" : "ip-track-card"}
              data-testid="ip-track-full"
              onClick={() => setTrack("full")}
            >
              <strong>{ui.trackFull}</strong>
              <span className="muted">{ui.trackFullHint}</span>
            </button>
          </div>
        </section>

        <div className="ip-progress" data-testid="ip-progress">
          <div className="ip-progress__bar">
            <div className="ip-progress__fill" style={{ width: `${progressPct}%` }} />
          </div>
          <p className="muted">
            {ui.progress}: {state.completedIds.length} / {blocks.length} ({progressPct}%)
          </p>
        </div>

        <nav className="ip-step-nav" aria-label={ui.stepsTitle} data-testid="ip-step-nav">
          {blocks.map((block, index) => {
            const active = index === state.stepIndex;
            const done = state.completedIds.includes(block.id);
            return (
              <button
                key={block.id}
                type="button"
                className={
                  active
                    ? "ip-step-pill ip-step-pill--active"
                    : done
                      ? "ip-step-pill ip-step-pill--done"
                      : "ip-step-pill"
                }
                data-testid={`ip-step-${block.id}`}
                onClick={() => goStep(index)}
                aria-current={active ? "step" : undefined}
              >
                {index + 1}. {block.title.replace(/^\d+\.\s*/, "")}
              </button>
            );
          })}
        </nav>

        {state.tourActive ? (
          <GsdReadableBlock className="ip-tour-banner warning-box" testId="ip-tour-banner">
            <p>{ui.tourHint}</p>
            <button type="button" className="btn-secondary" data-testid="ip-exit-tour" onClick={() => toggleTour(false)}>
              {ui.exitTour}
            </button>
          </GsdReadableBlock>
        ) : null}

        {activeBlock ? (
          <GsdReadableBlock className="ip-presenter" testId="ip-presenter-panel">
            <header className="ip-presenter__header">
              <h2 className="ip-presenter__title">{activeBlock.title}</h2>
              <p className="ip-presenter__meta muted">
                {ui.duration}: <strong>{activeBlock.duration}</strong>
                {state.completedIds.includes(activeBlock.id) ? (
                  <span className="ip-badge ip-badge--done" data-testid="ip-step-completed-badge">
                    {" "}
                    · {ui.stepDone}
                  </span>
                ) : (
                  <span className="ip-badge ip-badge--active"> · {ui.stepActive}</span>
                )}
              </p>
            </header>

            <p>
              <strong>{ui.objective}:</strong> {activeBlock.objective}
            </p>

            <div className="ip-presenter__say" data-testid="ip-say-message">
              <h3 className="section-h2">{ui.sayReady}</h3>
              <p className="ip-say-text">{activeBlock.sayMessage}</p>
            </div>

            <div className="ip-presenter__points">
              <h3 className="section-h2">{ui.keyPoints}</h3>
              <ul>
                {activeBlock.points.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>

            <div className="ip-presenter__screens">
              <h3 className="section-h2">{ui.screens}</h3>
              <ul className="ip-screen-list">
                {activeBlock.screens.map((screen) => (
                  <li key={`${screen.route}-${screen.label}`}>
                    <span>{screen.label}</span>
                    {screen.show ? <span className="muted"> — {screen.show}</span> : null}
                    <Link
                      to={screen.route}
                      className="btn-secondary btn-sm ip-open-screen"
                      data-testid={`ip-open-screen-${activeBlock.id}-${screen.route.replace(/\//g, "-")}`}
                    >
                      {ui.openScreen}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {primaryScreen ? (
              <p className="ip-next-action" data-testid="ip-next-action">
                <strong>{ui.nextAction}:</strong> {primaryScreen.label} → {ui.openScreen}
              </p>
            ) : null}

            <div className="ip-presenter__limits info-box">
              <h3 className="section-h2">{ui.limitsReminder}</h3>
              <ul>
                {activeBlock.limits.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            </div>

            <div className="ip-nav-actions btn-row">
              <button
                type="button"
                className="btn-secondary"
                data-testid="ip-prev-step"
                disabled={state.stepIndex <= 0}
                onClick={() => goStep(state.stepIndex - 1)}
              >
                {ui.prevStep}
              </button>
              <button type="button" className="btn-secondary" data-testid="ip-mark-complete" onClick={markComplete}>
                {ui.markComplete}
              </button>
              <button
                type="button"
                className="btn-primary"
                data-testid="ip-next-step"
                disabled={state.stepIndex >= blocks.length - 1}
                onClick={() => goStep(state.stepIndex + 1)}
              >
                {ui.nextStep}
              </button>
            </div>

            <div className="ip-tour-actions btn-row">
              {!state.tourActive ? (
                <button type="button" className="btn-primary-outline" data-testid="ip-start-tour" onClick={() => toggleTour(true)}>
                  {ui.startTour}
                </button>
              ) : null}
              <button type="button" className="btn-secondary" data-testid="ip-reset-demo" onClick={resetDemo}>
                {ui.resetDemo}
              </button>
            </div>
          </GsdReadableBlock>
        ) : null}

        <section className="ip-notes" data-testid="ip-notes-section">
          <h2 className="section-h2">{ui.notesTitle}</h2>

          <div className="ip-note-form">
            <label htmlFor="ip-note-type">{ui.noteType}</label>
            <select
              id="ip-note-type"
              data-testid="ip-note-type"
              value={draftNote.type}
              onChange={(e) => setDraftNote((d) => ({ ...d, type: e.target.value }))}
            >
              {noteTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>

            <label htmlFor="ip-note-text">{ui.noteText}</label>
            <textarea
              id="ip-note-text"
              data-testid="ip-note-text"
              rows={4}
              value={draftNote.text}
              onChange={(e) => setDraftNote((d) => ({ ...d, text: e.target.value }))}
            />

            <div className="ip-note-form__actions btn-row">
              <button type="button" className="btn-primary" data-testid="ip-add-note" onClick={addNote}>
                {ui.addNote}
              </button>
              {voiceSupported ? (
                <>
                  {dictating ? (
                    <p className="ip-dictation-active" data-testid="ip-dictation-active">
                      <span className="ip-dictation-active__dot" aria-hidden="true" />
                      <span className="ip-dictation-active__badge">{ui.dictationListening}</span>
                      {ui.dictationActive}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    className={dictating ? "btn-secondary ip-dictate-btn--active" : "btn-secondary"}
                    data-testid="ip-dictate-note"
                    onClick={dictating ? stopDictation : startDictation}
                  >
                    {dictating ? ui.stopDictation : ui.dictateNote}
                  </button>
                </>
              ) : (
                <p className="muted" data-testid="ip-voice-unavailable">
                  {ui.voiceUnavailable}
                </p>
              )}
            </div>
          </div>

          <ul className="ip-notes-list" data-testid="ip-notes-list">
            {stepNotes.length === 0 ? (
              <li className="muted">{ui.noNotes}</li>
            ) : (
              stepNotes.map((note) => (
                <li key={note.id} className="ip-note-item" data-testid={`ip-note-item-${note.id}`}>
                  {editingNoteId === note.id ? (
                    <textarea
                      defaultValue={note.text}
                      data-testid={`ip-note-edit-${note.id}`}
                      onBlur={(e) => updateNote(note.id, { text: e.target.value.trim() })}
                    />
                  ) : (
                    <>
                      <span className="ip-note-type">{getNoteTypeLabel(note.type, locale)}</span>
                      <p>{note.text}</p>
                      <div className="btn-row">
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={() => setEditingNoteId(note.id)}
                        >
                          {ui.editNote}
                        </button>
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          data-testid={`ip-delete-note-${note.id}`}
                          onClick={() => deleteNote(note.id)}
                        >
                          {ui.deleteNote}
                        </button>
                      </div>
                    </>
                  )}
                </li>
              ))
            )}
          </ul>
        </section>

        <section className="ip-limits" data-testid="ip-limits-section">
          <h2 className="section-h2">{ui.limitsTitle}</h2>
          <ul>
            {limits.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
          <h3 className="section-h2">{ui.phase2Title}</h3>
          <ul>
            {phase2.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </section>

        <section className="ip-report" data-testid="ip-report-section">
          <h2 className="section-h2">{ui.reportTitle}</h2>
          <div className="btn-row">
            <button type="button" className="btn-primary" data-testid="ip-generate-report" onClick={generateReport}>
              {ui.generateReport}
            </button>
            <button type="button" className="btn-secondary" data-testid="ip-copy-report" onClick={handleCopyReport}>
              {ui.copyReport}
            </button>
            <button
              type="button"
              className="btn-secondary"
              data-testid="ip-export-markdown"
              onClick={() => {
                const md = reportText || generateReport();
                downloadText(
                  locale === "en" ? "karate-improvement-report.md" : "karate-rapport-amelioration.md",
                  md,
                  "text/markdown",
                );
              }}
            >
              {ui.exportMarkdown}
            </button>
            <button
              type="button"
              className="btn-secondary"
              data-testid="ip-export-json"
              onClick={() => {
                downloadText(
                  locale === "en" ? "karate-improvement-report.json" : "karate-rapport-amelioration.json",
                  exportJson(),
                  "application/json",
                );
              }}
            >
              {ui.exportJson}
            </button>
            <button type="button" className="btn-secondary" data-testid="ip-reset-notes" onClick={resetNotes}>
              {ui.resetNotes}
            </button>
          </div>
          {copyMsg ? <p className="ip-copy-msg" data-testid="ip-copy-msg">{copyMsg}</p> : null}
          {reportText ? (
            <pre className="ip-report-preview" data-testid="ip-report-preview">
              {reportText}
            </pre>
          ) : null}
        </section>
      </PageSection>
    </AdminGuard>
  );
}
